import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import trackingService, {
  WebhookLogRow,
  WebhookLogStatus,
} from '../../services/tracking.service';
import { TanTable } from '../../components/TanTable';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';

/**
 * Tracing → Webhook Activity admin page.
 *
 * One row per (webhook, pronumber) pair. fields_updated lists the CMS
 * columns sync_pronumber actually wrote to cmsapi after freeze rules +
 * the unchanged-field diff. Pre-fan-out failures (signature rejected,
 * bad payload, no order_tracking_link rows for the shipment) appear
 * as rows with pronumber=null and a distinguishing status.
 *
 * Data loading: one fetch for the selected time window (default 24h),
 * up to 10000 rows. Sort / per-column filter / Excel export run inside
 * TanTable on the client. The time-window dropdown is the only
 * server-side scope control; everything else is column-level.
 */

const STATUS_BADGE_CLASSES: Record<WebhookLogStatus, string> = {
  success: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  no_changes: 'bg-gray-100 dark:bg-gray-700 text-label',
  error: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  no_link: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  signature_rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  invalid_payload: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
  ignored: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
};

const STATUS_LABELS: Record<WebhookLogStatus, string> = {
  success: 'Updated',
  no_changes: 'No changes',
  error: 'Error',
  no_link: 'No link',
  signature_rejected: 'Sig rejected',
  invalid_payload: 'Bad payload',
  ignored: 'Ignored',
};

// Time-window scopes for the single server-side data control. 24h is the
// default — most webhook debugging happens on the most recent traffic. 7d
// matches the table's retention so anything older than that is gone anyway.
const WINDOW_OPTIONS: { label: string; hours: number }[] = [
  { label: 'Last 1h', hours: 1 },
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 3 days', hours: 72 },
  { label: 'Last 7 days', hours: 168 },
];

const DEFAULT_WINDOW_HOURS = 24;
const FETCH_LIMIT = 10000;

const formatReceived = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const WebhookActivity: React.FC = () => {
  const [rows, setRows] = useState<WebhookLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [windowHours, setWindowHours] = useState<number>(DEFAULT_WINDOW_HOURS);
  const [drawerPro, setDrawerPro] = useState<number | null>(null);

  const openDrawer = useCallback((pronumber: number) => setDrawerPro(pronumber), []);
  const closeDrawer = useCallback(() => setDrawerPro(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = new Date(Date.now() - windowHours * 3600 * 1000).toISOString();
      const data = await trackingService.listWebhookLog({
        since,
        limit: FETCH_LIMIT,
        offset: 0,
      });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load webhook log');
    } finally {
      setLoading(false);
    }
  }, [windowHours]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<ColumnDef<WebhookLogRow>[]>(
    () => [
      {
        accessorKey: 'received_at',
        header: 'Received',
        cell: (info) => formatReceived(info.getValue() as string | null),
        size: 170,
      },
      {
        accessorKey: 'event',
        header: 'Event',
        cell: (info) => {
          const v = info.getValue() as string | null;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
              {v}
            </span>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'shipment_id',
        header: 'Shipment',
        cell: (info) => {
          const v = info.getValue() as string | null;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span
              className="text-xs cursor-pointer select-all"
              title="Click to copy"
              onClick={() => navigator.clipboard?.writeText(v)}
            >
              {v}
            </span>
          );
        },
        size: 280,
      },
      {
        accessorKey: 'container_number',
        header: 'Container',
        cell: (info) => {
          const v = info.getValue() as string | null;
          return v ? (
            <span>{v}</span>
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
        size: 130,
      },
      {
        accessorKey: 'pronumber',
        header: 'Pro #',
        cell: (info) => {
          const v = info.getValue() as number | null;
          return v != null ? (
            <button
              type="button"
              onClick={() => openDrawer(v)}
              className="text-blue-600 dark:text-blue-400 hover:underline"
              title="Open order"
            >
              {v}
            </button>
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'order_rail',
        header: 'Rail',
        cell: (info) => {
          const v = info.getValue() as string | null;
          return v || <span className="text-gray-400">—</span>;
        },
        size: 90,
      },
      {
        accessorKey: 'order_scac',
        header: 'SCAC',
        cell: (info) => {
          const v = info.getValue() as string | null;
          return v || <span className="text-gray-400">—</span>;
        },
        size: 90,
      },
      {
        // accessorFn returns the human label so the dropdown filter shows
        // "Updated", "No changes", etc. instead of raw "success", "no_changes".
        // The cell still uses original.status for the badge color lookup.
        id: 'status',
        accessorFn: (row) => STATUS_LABELS[row.status] || row.status,
        header: 'Status',
        cell: (info) => {
          const row = info.row.original;
          return (
            <div>
              <span
                className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                  STATUS_BADGE_CLASSES[row.status] || ''
                }`}
              >
                {STATUS_LABELS[row.status] || row.status}
              </span>
              {row.error_message && (
                <div
                  className="text-xs text-red-600 dark:text-red-400 mt-0.5 max-w-xs truncate"
                  title={row.error_message}
                >
                  {row.error_message}
                </div>
              )}
            </div>
          );
        },
        size: 130,
      },
      {
        accessorKey: 'fields_updated',
        header: 'Fields Updated',
        cell: (info) => {
          const v = info.getValue() as string | null;
          const count = info.row.original.field_count;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <div className="max-w-md">
              <div className="text-xs text-muted mb-0.5">
                {count} field{count === 1 ? '' : 's'}
              </div>
              <div className="flex flex-wrap gap-1">
                {v.split(',').map((f) => (
                  <span
                    key={f}
                    className="text-xs px-1 py-0.5 bg-gray-100 dark:bg-gray-700 text-label rounded"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          );
        },
        size: 280,
      },
      {
        accessorKey: 'duration_ms',
        header: 'Duration',
        cell: (info) => {
          const v = info.getValue() as number | null;
          return (
            <span className="text-muted text-xs">
              {formatDuration(v)}
            </span>
          );
        },
        size: 90,
      },
      {
        // accessorFn for dropdown values ("Yes" / "No") + sortable boolean.
        id: 'signature_valid',
        accessorFn: (row) => (row.signature_valid ? 'Yes' : 'No'),
        header: 'Signed',
        cell: (info) => {
          const v = info.row.original.signature_valid;
          return v ? (
            <CheckCircleIcon className="inline h-4 w-4 text-green-600 dark:text-green-400" />
          ) : (
            <ExclamationTriangleIcon className="inline h-4 w-4 text-red-600 dark:text-red-400" />
          );
        },
        size: 80,
      },
    ],
    [openDrawer],
  );

  return (
    <div className="p-4 pb-24">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-heading">Webhook Activity</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Incoming gwiTrace webhook events, one row per (webhook, pronumber)
              pair. <strong>Fields Updated</strong> lists the CMS columns
              actually written by that webhook for that pronumber. Rows are
              pruned after 7 days by the gwitrace_webhook_log_cleanup job.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-muted">Window</label>
            <select
              value={windowHours}
              onChange={(e) => setWindowHours(parseInt(e.target.value, 10))}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-heading"
            >
              {WINDOW_OPTIONS.map((opt) => (
                <option key={opt.hours} value={opt.hours}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      {total >= FETCH_LIMIT && (
        <div className="mb-2 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          Showing the first {FETCH_LIMIT.toLocaleString()} rows in this window
          (total is {total.toLocaleString()}). Pick a shorter window to see the
          most recent activity.
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="webhook-activity-table"
        cardTitle="Webhook Activity"
        searchPlaceholder="Search webhook events…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        // Some cells (status error message, fields_updated chip cloud) render
        // 2+ lines, which fights TanTable's default fixed-height virtualization
        // and produces visible row overlap. Disable virtualization so rows
        // size to their content — fine for our 10k window cap.
        disableVirtualization={true}
        columnFilterTypes={{
          received_at: 'text',
          shipment_id: 'text',
          container_number: 'text',
          pronumber: 'text',
          fields_updated: 'text',
          duration_ms: 'text',
        }}
        defaultSorting={[{ id: 'received_at', desc: true }]}
      />

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default WebhookActivity;
